import axios from 'axios';
import { doc, updateDoc, setDoc, getDoc, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { checkApiCallCount } from './Access'; 
import { marked } from 'marked';

// Firebase Functions API base URL
// Use local emulator ONLY when explicitly on localhost dev server; otherwise use production
const getFunctionsBaseUrl = () => {
  const host = window.location.hostname;
  const port = window.location.port;

  const isLocalHost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host === '::1';
  const isReactDevServer = port === '3000';

  if (isLocalHost && isReactDevServer) {
    return 'http://localhost:5001/ai-d-ce511/us-central1/api';
  }
  // Production URL
  return 'https://us-central1-ai-d-ce511.cloudfunctions.net/api';
};

const FUNCTIONS_BASE_URL = getFunctionsBaseUrl();

const incrementApiCallCount = async (userDocId) => {
  const userDocRef = doc(db, 'userData', userDocId);
  try {
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      const userData = userDocSnap.data();

      if (!userData.apiCallCount) {
        // Initialize the field if it doesn't exist
        await updateDoc(userDocRef, { apiCallCount: 1 });
      } else {
        // Increment the existing field
        await updateDoc(userDocRef, { apiCallCount: increment(1) });
      }
    } else {
      console.error('User document does not exist!');
    }
  } catch (error) {
    console.error('Error incrementing API call count:', error);
  }
};


const logMessageToFirestore = async (userDocId, input, botMessage) => {
  try {
    const userDocRef = doc(db, 'recordedData', userDocId);

    // Check if the document exists
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      // Document exists; append the message to the messages array
      await updateDoc(userDocRef, {
        messages: arrayUnion(input),
        botMessages : arrayUnion(botMessage)
      });
    } else {
      // Document does not exist; create it with the messages array
      await setDoc(userDocRef, {
        messages: [input],
        botMessages: [botMessage]
      });
    }
  } catch (error) {
    console.error('Error logging message to Firestore:', error);
    throw new Error('Failed to log message to Firestore.');
  }
};


export const getChatResponse = async (userDocId, input, customMessage = '', setShowModal) => {
  try {
    const hasExceededLimit = await checkApiCallCount(userDocId);

    if (hasExceededLimit) {
      setShowModal(true); // Show the modal popup
      throw new Error('API call limit exceeded.');
    }

    // Call secure Firebase Functions endpoint instead of direct OpenAI API
    let response;
    try {
      response = await axios.post(
        `${FUNCTIONS_BASE_URL}/openai/chat`,
      {
          userDocId,
          input,
          customMessage,
        model: 'gpt-4o',
          maxTokens: 2000,
        }
      );
    } catch (err) {
      // If running on live site but somehow pointed to localhost, retry against production URL
      const isConnRefused = err?.message?.includes('Network Error') || err?.message?.includes('ECONN') || err?.code === 'ERR_NETWORK';
      const isLocalBase = FUNCTIONS_BASE_URL.includes('localhost:5001');
      if (isConnRefused && isLocalBase) {
        response = await axios.post(
          `https://us-central1-ai-d-ce511.cloudfunctions.net/api/openai/chat`,
          {
            userDocId,
            input,
            customMessage,
            model: 'gpt-4o',
            maxTokens: 2000,
          }
        );
      } else {
        throw err;
      }
    }

    // Response is already logged on the server side
    return response.data.response;
  } catch (error) {
    console.error('Error:', error.response ? error.response.data : error.message);
    if (error.response?.status === 429) {
      if (setShowModal) setShowModal(true);
      throw new Error('API call limit exceeded.');
    }
    throw new Error('Sorry, I could not process your request at this time.');
  }
};

export const getChatResponseFree = async (userDocId, input, customMessage = '', setShowModal) => {
  try {
    // Call secure Firebase Functions endpoint instead of direct OpenAI API
    let response;
    try {
      response = await axios.post(
        `${FUNCTIONS_BASE_URL}/openai/chat-free`,
      {
          input,
          customMessage,
        model: 'gpt-4o',
          maxTokens: 2000,
        }
      );
    } catch (err) {
      const isConnRefused = err?.message?.includes('Network Error') || err?.message?.includes('ECONN') || err?.code === 'ERR_NETWORK';
      const isLocalBase = FUNCTIONS_BASE_URL.includes('localhost:5001');
      if (isConnRefused && isLocalBase) {
        response = await axios.post(
          `https://us-central1-ai-d-ce511.cloudfunctions.net/api/openai/chat-free`,
          {
            input,
            customMessage,
            model: 'gpt-4o',
            maxTokens: 2000,
          }
        );
      } else {
        throw err;
      }
    }

    return response.data.response;
  } catch (error) {
    console.error('Error:', error.response ? error.response.data : error.message);
    throw new Error('Sorry, I could not process your request at this time.');
  }
};


const formatResponse = (response) => {
  const htmlContent = marked(response);
  const div = document.createElement('div');
  div.innerHTML = htmlContent;

  // Ensure innerText is being called on the created div element
  const textContent = div.innerText || '';

  // Split text content into lines by newline characters
  const lines = textContent.split('\n').filter(line => line.trim() !== '');

  return lines;
};



// Function to get the chat response
export const getShortChatResponse = async (userDocId, input, userDoc, myColleges, customMessage = '', setShowModal) => {
  try {
    const hasExceededLimit = await checkApiCallCount(userDocId);

    if (hasExceededLimit) {
      setShowModal(true); // Show the modal popup
      throw new Error('API call limit exceeded.');
    }

    // Call secure Firebase Functions endpoint instead of direct OpenAI API
    let response;
    try {
      response = await axios.post(
        `${FUNCTIONS_BASE_URL}/openai/chat-short`,
        {
          userDocId,
          input,
          userDoc,
          myColleges,
          customMessage,
        model: 'gpt-4o-mini',
          maxTokens: 300,
        }
      );
    } catch (err) {
      const isConnRefused = err?.message?.includes('Network Error') || err?.message?.includes('ECONN') || err?.code === 'ERR_NETWORK';
      const isLocalBase = FUNCTIONS_BASE_URL.includes('localhost:5001');
      if (isConnRefused && isLocalBase) {
        response = await axios.post(
          `https://us-central1-ai-d-ce511.cloudfunctions.net/api/openai/chat-short`,
          {
            userDocId,
            input,
            userDoc,
            myColleges,
            customMessage,
            model: 'gpt-4o-mini',
            maxTokens: 300,
          }
        );
      } else {
        throw err;
      }
    }

    // Response is already logged on the server side
    const rawResponse = response.data.response;

    // Format the response
    const formattedResponse = formatResponse(rawResponse);

    return rawResponse; // Return the formatted response as an array
  } catch (error) {
    console.error('Error:', error.response ? error.response.data : error.message);
    if (error.response?.status === 429) {
      if (setShowModal) setShowModal(true);
      throw new Error('API call limit exceeded.');
    }
    throw new Error('Sorry, I could not process your request at this time.');
  }
};


export const getShorterChatResponse = async (userDocId, input, setShowModal) => {
  try {
    const hasExceededLimit = await checkApiCallCount(userDocId);

    if (hasExceededLimit) {
      setShowModal(true); // Show the modal popup
      throw new Error('API call limit exceeded.');
    }

    // Call secure Firebase Functions endpoint instead of direct OpenAI API
    let response;
    try {
      response = await axios.post(
        `${FUNCTIONS_BASE_URL}/openai/chat-shorter`,
      {
          userDocId,
          input,
        model: 'gpt-4o-mini',
          maxTokens: 300,
        }
      );
    } catch (err) {
      const isConnRefused = err?.message?.includes('Network Error') || err?.message?.includes('ECONN') || err?.code === 'ERR_NETWORK';
      const isLocalBase = FUNCTIONS_BASE_URL.includes('localhost:5001');
      if (isConnRefused && isLocalBase) {
        response = await axios.post(
          `https://us-central1-ai-d-ce511.cloudfunctions.net/api/openai/chat-shorter`,
          {
            userDocId,
            input,
            model: 'gpt-4o-mini',
            maxTokens: 300,
          }
        );
      } else {
        throw err;
      }
    }

    // Response is already logged on the server side
    const rawResponse = response.data.response;

    // Format the response
    const formattedResponse = formatResponse(rawResponse);

    return rawResponse; // Return the formatted response as an array
  } catch (error) {
    console.error('Error:', error.response ? error.response.data : error.message);
    if (error.response?.status === 429) {
      if (setShowModal) setShowModal(true);
      throw new Error('API call limit exceeded.');
    }
    throw new Error('Sorry, I could not process your request at this time.');
  }
};
